// ===== AUTH SELECTORS =====
// src/features/auth/state/auth-state/authSelectors.ts

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { Permission } from '../../types/auth.types';

// Basic selectors are already in authSlice.ts
// Here are additional complex selectors

export const selectUserFullName = createSelector(
  [(state: RootState) => state.auth.user],
  (user) => {
    if (!user) return '';
    return `${user.firstName} ${user.lastName}`.trim();
  }
);

export const selectUserInitials = createSelector(
  [(state: RootState) => state.auth.user],
  (user) => {
    if (!user) return '';
    const firstInitial = user.firstName?.[0] || '';
    const lastInitial = user.lastName?.[0] || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }
);

export const selectHasPermission = (permission: string) =>
  createSelector(
    [(state: RootState) => state.auth.permissions],
    (permissions: Permission[]) => {
      return permissions.some(p => p.action === permission);
    }
  );

export const selectHasAnyPermission = (requiredPermissions: string[]) =>
  createSelector(
    [(state: RootState) => state.auth.permissions],
    (permissions: Permission[]) => {
      return requiredPermissions.some(required =>
        permissions.some(p => p.action === required)
      );
    }
  );

export const selectHasAllPermissions = (requiredPermissions: string[]) =>
  createSelector(
    [(state: RootState) => state.auth.permissions],
    (permissions: Permission[]) => {
      return requiredPermissions.every(required =>
        permissions.some(p => p.action === required)
      );
    }
  );

export const selectSessionExpired = createSelector(
  [(state: RootState) => state.auth.session],
  (session) => {
    if (!session || !session.expiresAt) return false;
    return new Date(session.expiresAt) < new Date();
  }
);

export const selectTimeUntilSessionExpiry = createSelector(
  [(state: RootState) => state.auth.session],
  (session) => {
    if (!session || !session.expiresAt) return 0;
    const expiresAt = new Date(session.expiresAt);
    const now = new Date();
    return Math.max(0, expiresAt.getTime() - now.getTime());
  }
);
